import { Box, Typography } from "@mui/material";
import { useContext, useMemo } from "react";
import CustomButton from "../../../UIElements/CustomButton";
import { LayoutBarContext, SignUpContext } from "../../../../contexts";
import "../../../components-styles.scss";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { passwordRegex } from "../../../../consts";
import PasswordInput from "../../../UIElements/PasswordInput/PasswordInput";
import PasswordRequirementsList from "../../../Common/PasswordRequirementsList";
import { signup } from "../../../../services";
import ConfirmationSuccessSignup from "../ConfirmationSuccessSignup";
import { useNavigate } from "react-router-dom";
import { useFetch } from "../../../../hooks";
import { RoutesEnum } from "../../../../router/routes";

type UserFormProps = {
    goto: (step: number) => void;
    stepNum: number;
};

const validationSchema = yup.object({
    password: yup
        .string()
        .matches(passwordRegex, "Veuillez renseigner un mot de passe valide")
        .required("Veuillez renseigner un mot de passe"),
    confirmPassword: yup
        .string()
        .oneOf([yup.ref("password")], "Les mots de passe ne correspondent pas")
        .required("Confirmation du mot de passe requise"),
});

export default function PasswordForm({ goto, stepNum }: UserFormProps) {
    const { updateSignupForm, ...signupForm } = useContext(SignUpContext);
    const { updateContent } = useContext(LayoutBarContext);
    const navigate = useNavigate();

    const { fetcher } = useFetch();

    const {
        handleSubmit,
        formState: { errors },
        control,
        watch,
    } = useForm({
        defaultValues: {
            password: signupForm?.signupUser.password ?? "",
        },
        resolver: yupResolver(validationSchema),
    });

    const password = watch("password");
    const confirmedPassword = watch("confirmPassword");

    const isValidPassword = useMemo(
        () => passwordRegex.test(password ?? ""),
        [password]
    );

    const onSubmit = async (data: any) => {
        await updateSignupForm({
            ...signupForm,
            step: stepNum,
            signupUser: {
                ...signupForm.signupUser,
                ...data,
            },
        });
        const doSignup = async () => {
            const signupRes = await signup({
                ...signupForm.signupUser,
                password: password,
                confirmPassword: confirmedPassword,
            });
            if (!signupRes.detail) {
                updateContent(<ConfirmationSuccessSignup />);
                navigate("/" + RoutesEnum.successSignup);
            }
        };
        fetcher(doSignup());
    };

    return (
        <Box className="signup-form-root">
            <Box className="signup-form-title-container">
                <Typography className="signup-form-title">
                    Votre mot de passe
                </Typography>
                <Typography className="signup-form-sub-title">
                    Créez votre mot de passe
                </Typography>
            </Box>

            <form onSubmit={handleSubmit(onSubmit)} className="signup-form">
                <Controller
                    name="password"
                    control={control}
                    render={({ field: { ref, ...others } }) => (
                        <PasswordInput
                            fullWidth={true}
                            label={"Mot de passe"}
                            {...others}
                            havePasswordChecker={true}
                            isValidPassword={isValidPassword}
                            errortext={errors.password?.message ?? ""}
                            showvisibility={(password || "").length}
                        />
                    )}
                />
                <Controller
                    name="confirmPassword"
                    control={control}
                    render={({ field: { ref, ...others } }) => (
                        <PasswordInput
                            fullWidth={true}
                            label={"Confirmation de mot de passe *"}
                            {...others}
                            havePasswordChecker={true}
                            errortext={errors.confirmPassword?.message ?? ""}
                            showvisibility={(confirmedPassword || "").length}
                        />
                    )}
                />
                <PasswordRequirementsList password={password} />
                <CustomButton
                    type="submit"
                    label="Étape suivante"
                    customsx={{
                        marginLeft: "auto",
                        marginRight: "auto",
                        marginTop: "3.5rem",
                        marginBottom: "auto",
                    }}
                />
            </form>
        </Box>
    );
}
